import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PseudoInputButton,
  PseudoInputIcon,
  PseudoInputLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@1trading/react';
import { HTTPError } from 'ky';
import { Banknote, Check, ChevronDown } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { FieldError } from '@/components/ui/field-error';
import { usePlatformBalances } from '@/hooks/queries/system/use-platform-balances';
import {
  ACCOUNT_HOLDER_ID_LENGTH,
  createManualTransaction,
  MANUAL_AMOUNT_PATTERN,
  MANUAL_TRANSACTION_ACTIONS,
  MANUAL_TRANSACTION_REASONS,
  type ManualTransactionAction,
  type ManualTransactionReason,
} from '@/services/api/funds-transactions';

const REQUIRED_MESSAGE = 'This field is required';
const HTTP_FORBIDDEN = 403;

type FieldName = 'accountHolderId' | 'action' | 'reason' | 'currency' | 'amount' | 'note';
type FieldErrors = Partial<Record<FieldName, string>>;

interface FormState {
  accountHolderId: string;
  action: ManualTransactionAction | '';
  reason: ManualTransactionReason | '';
  currency: string;
  amount: string;
  note: string;
}

const INITIAL_FORM: FormState = { accountHolderId: '', action: '', reason: '', currency: '', amount: '', note: '' };

// Deliberate fixes over legacy: the 36-char PID and amount-format rules
// are validated client-side too, mirroring the API's checks.
const validateForm = (form: FormState): FieldErrors => ({
  ...(form.accountHolderId.trim() === ''
    ? { accountHolderId: REQUIRED_MESSAGE }
    : form.accountHolderId.trim().length !== ACCOUNT_HOLDER_ID_LENGTH
      ? { accountHolderId: `Account ID must be exactly ${ACCOUNT_HOLDER_ID_LENGTH} characters` }
      : {}),
  ...(form.action === '' ? { action: REQUIRED_MESSAGE } : {}),
  ...(form.reason === '' ? { reason: REQUIRED_MESSAGE } : {}),
  ...(form.currency.trim() === '' ? { currency: REQUIRED_MESSAGE } : {}),
  ...(form.amount.trim() === ''
    ? { amount: REQUIRED_MESSAGE }
    : MANUAL_AMOUNT_PATTERN.test(form.amount.trim())
      ? {}
      : { amount: 'Amount must be a positive number like 123 or 123.45 (no sign, no commas)' }),
  ...(form.note.trim() === '' ? { note: REQUIRED_MESSAGE } : {}),
});

interface ManualTransactionDialogProps {
  onClose: () => void;
  /** Refetch the transactions list after a successful create */
  onCreated: () => void;
}

// Mounted only while open, so the form resets on every open/close (deliberate fix
// over legacy, which kept stale values after submit or close).
export const ManualTransactionDialog = ({ onClose, onCreated }: ManualTransactionDialogProps) => {
  const fieldId = useId();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [currencyQuery, setCurrencyQuery] = useState('');

  // Deliberate fix: legacy pulled the currency list from the PUBLIC
  // crypto-info getPrices endpoint. Suggestions come from the internal platform
  // balances instead; free-text entry stays possible (e.g. fiat codes the
  // platform holds no balance in, entered via the "Use ..." option). A failed
  // suggestions query only leaves the list empty — never blocks the form.
  const balances = usePlatformBalances();
  const currencyOptions = useMemo(() => {
    const assets = new Set<string>();
    for (const row of balances.data?.combined ?? []) assets.add(row.asset.toUpperCase());
    for (const row of balances.data?.futures ?? []) assets.add(row.asset.toUpperCase());
    return [...assets].sort();
  }, [balances.data]);
  const filteredCurrencyOptions = useMemo(() => {
    const query = currencyQuery.trim().toUpperCase();
    return query === '' ? currencyOptions : currencyOptions.filter((option) => option.includes(query));
  }, [currencyOptions, currencyQuery]);

  const setField = (field: FieldName, value: string) => setForm((previous) => ({ ...previous, [field]: value }));
  const selectCurrency = (currency: string) => {
    setField('currency', currency);
    setCurrencyQuery('');
    setCurrencyOpen(false);
  };
  const handleCurrencyOpenChange = (open: boolean) => {
    setCurrencyOpen(open);
    if (!open) setCurrencyQuery('');
  };

  const handleSubmit = async () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    // Narrowed by validation above
    if (form.action === '' || form.reason === '') return;
    const { accountHolderId, action, reason, note } = form;
    const amount = form.amount.trim();
    const currency = form.currency.trim().toUpperCase();

    const justification = await confirmAction({
      title: `Confirm ${action} ${amount} ${currency}`,
      description: `Proceeding with this action will ${action} ${accountHolderId.trim()} with ${amount} ${currency}. Your action here will be logged and will be auditable.`,
      actionLabel: `${action} ${amount} ${currency}`,
      onConfirm: async (comment) => {
        try {
          await createManualTransaction({
            accountHolderId: accountHolderId.trim(),
            currencyCode: currency,
            amount,
            action,
            reason,
            note,
            comment,
          });
        } catch (error) {
          // The button renders for everyone, but bop-api gates on its staff allowlist
          if (error instanceof HTTPError && error.response.status === HTTP_FORBIDDEN) {
            throw new Error('You are not authorised to create manual transactions');
          }
          throw error;
        }
      },
    });
    if (justification === null) return;
    toast.success(`Manual transaction created: ${action} ${amount} ${currency}`);
    onCreated();
    onClose();
  };

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="size-5" />
            Create Manual Transaction
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-account`}>Account ID</Label>
            <Input id={`${fieldId}-account`} onChange={(event) => setField('accountHolderId', event.target.value)} value={form.accountHolderId} />
            <FieldError message={errors.accountHolderId} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-action`}>Credit / Debit</Label>
            <Select onValueChange={(value) => setField('action', value)} value={form.action || undefined}>
              <SelectTrigger id={`${fieldId}-action`}>
                <SelectValue placeholder="Select an action" />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_TRANSACTION_ACTIONS.map((action) => (
                  <SelectItem key={action} value={action}>
                    {action}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError message={errors.action} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-reason`}>Reason</Label>
            <Select onValueChange={(value) => setField('reason', value)} value={form.reason || undefined}>
              <SelectTrigger id={`${fieldId}-reason`}>
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {MANUAL_TRANSACTION_REASONS.map((reason) => (
                  <SelectItem key={reason} value={reason}>
                    {reason}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FieldError message={errors.reason} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-currency`}>Currency</Label>
            <Popover onOpenChange={handleCurrencyOpenChange} open={currencyOpen}>
              <PopoverTrigger asChild>
                <PseudoInputButton className="border-input bg-card text-card-foreground" id={`${fieldId}-currency`}>
                  <PseudoInputLabel className={cn(form.currency === '' && 'text-muted-foreground')}>{form.currency || 'Select a currency'}</PseudoInputLabel>
                  <PseudoInputIcon>
                    <ChevronDown />
                  </PseudoInputIcon>
                </PseudoInputButton>
              </PopoverTrigger>
              <PopoverContent className="p-0" stretch>
                <Command shouldFilter={false}>
                  <CommandInput onValueChange={setCurrencyQuery} placeholder="Search currency..." value={currencyQuery} />
                  {/* The dialog's own scroll lock (react-remove-scroll) intercepts wheel events on
                      this list because it's portaled outside the Dialog's locked subtree — stopping
                      propagation here keeps the wheel event local so native scroll still runs. */}
                  <CommandList onWheel={(event) => event.stopPropagation()}>
                    {filteredCurrencyOptions.length === 0 && currencyQuery.trim() !== '' ? (
                      <CommandGroup>
                        <CommandItem onSelect={() => selectCurrency(currencyQuery.trim().toUpperCase())} value={currencyQuery}>
                          Use "{currencyQuery.trim().toUpperCase()}"
                        </CommandItem>
                      </CommandGroup>
                    ) : (
                      <CommandEmpty className="py-3 text-center text-base">No currencies found</CommandEmpty>
                    )}
                    <CommandGroup>
                      {filteredCurrencyOptions.map((option) => (
                        <CommandItem className="gap-2" key={option} onSelect={() => selectCurrency(option)} value={option}>
                          <Check className={cn('shrink-0 size-4', form.currency === option ? 'opacity-100' : 'opacity-0')} />
                          {option}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <FieldError message={errors.currency} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-amount`}>Amount</Label>
            <Input id={`${fieldId}-amount`} inputMode="decimal" onChange={(event) => setField('amount', event.target.value)} value={form.amount} />
            <FieldError message={errors.amount} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${fieldId}-note`}>Notes</Label>
            <Textarea id={`${fieldId}-note`} onChange={(event) => setField('note', event.target.value)} value={form.note} />
            <FieldError message={errors.note} />
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose} type="button" variant="outline">
            Close
          </Button>
          <Button onClick={handleSubmit} type="button" variant="primary">
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
ManualTransactionDialog.displayName = 'ManualTransactionDialog';
